import React, { useMemo, useState } from "react";
import { CSVLink } from "react-csv";
import { useSubheader } from "../../../../_metronic/layout";
import { CheckInTabs } from "../Components/CheckInTabs";
import { KioskDayPicker } from "../Components/KioskDayPicker";
import { useKioskDay } from "../Components/useKioskDay";
import {
  fetchVisits,
  formatDuration,
  formatScanTime,
  studentName,
  visitDurationMinutes,
  visitInstant,
  visitTotals,
} from "../Components/attendanceCrud";

const FORBIDDEN =
  "You do not have permission to view competition visits for this branch. Ask an Admin or Branch Head.";
const FALLBACK = "Could not load competition visits for that day.";

/**
 * The literal string, in one place, used everywhere a missing checkout is
 * shown. Not a blank, not a dash, not a guessed closing time: a student with no
 * checkout is a fact about the day, and the screen says so out loud.
 */
const NO_CHECKOUT = "No Checkout";

const FILTERS = {
  ALL: "all",
  MISSING: "missing",
  COMPLETE: "complete",
};

const CSV_HEADERS = [
  { label: "Student", key: "student" },
  { label: "Student user ID", key: "studentUserId" },
  { label: "Batch ID", key: "batchId" },
  { label: "Checked in", key: "checkedIn" },
  { label: "Checked out", key: "checkedOut" },
  { label: "Duration (minutes)", key: "durationMinutes" },
];

export function CompetitionVisitsList() {
  const subheader = useSubheader();
  subheader.setTitle("Competition Visits");

  const state = useKioskDay(fetchVisits, FORBIDDEN, FALLBACK);
  const { rows: visits, date, isLoading, errorMessage, hasLoaded } = state;

  const [filter, setFilter] = useState(FILTERS.ALL);

  const ordered = useMemo(
    () =>
      [...visits].sort((a, b) => visitInstant(a) - visitInstant(b)),
    [visits]
  );

  const missingCount = useMemo(
    () => ordered.filter((visit) => !visit.checked_out_at).length,
    [ordered]
  );

  const shown = useMemo(() => {
    if (filter === FILTERS.MISSING) {
      return ordered.filter((visit) => !visit.checked_out_at);
    }
    if (filter === FILTERS.COMPLETE) {
      return ordered.filter((visit) => Boolean(visit.checked_out_at));
    }
    return ordered;
  }, [ordered, filter]);

  // Totals describe the rows on screen, so a filtered view's total is the
  // total of what is shown — and it still carries its own exclusion count.
  const totals = useMemo(() => visitTotals(shown), [shown]);

  /**
   * A missing checkout exports as an *empty* cell — in both the checkout and
   * the duration column.
   *
   * Not `0`, and not a placeholder. A `0` in a duration column is silently
   * correct-looking arithmetic: it sums, it averages, it drags a mean down,
   * and nobody downstream ever discovers that the number was invented here. An
   * empty cell is ignored by SUM and AVERAGE in every spreadsheet, which is
   * exactly the behaviour these rows deserve.
   */
  const csvData = useMemo(
    () =>
      shown.map((visit) => {
        const minutes = visitDurationMinutes(visit);
        return {
          student: studentName(visit) || `Student #${visit.student_user_id}`,
          studentUserId: visit.student_user_id,
          batchId: visit.batch_id,
          checkedIn: visit.checked_in_at || "",
          checkedOut: visit.checked_out_at || "",
          durationMinutes: minutes === null ? "" : minutes,
        };
      }),
    [shown]
  );

  const branchName =
    state.currentBranch && state.currentBranch.branch_name
      ? state.currentBranch.branch_name
      : "branch";

  return (
    <div className="card card-custom">
      <div className="card-header border-0 py-5">
        <h3 className="card-title align-items-start flex-column">
          <span className="card-label font-weight-bolder text-dark">
            Competition Visits
          </span>
          <span className="text-muted mt-3 font-weight-bold font-size-sm">
            Who was in the building at {branchName}, and when.
          </span>
        </h3>
      </div>

      <div className="card-body pt-0">
        <CheckInTabs />

        <KioskDayPicker
          label="Date"
          date={date}
          setDate={state.setDate}
          onRefresh={state.refresh}
          isLoading={isLoading}
        />

        {errorMessage && (
          <div
            className="mb-5 alert alert-custom alert-light-danger"
            role="alert"
          >
            <div className="alert-text">{errorMessage}</div>
          </div>
        )}

        {!state.branchReady && !errorMessage && (
          <p className="text-muted text-center py-10">
            Pick a branch in the top bar to see its competition visits.
          </p>
        )}

        {state.branchReady && !state.authToken && !errorMessage && (
          <p className="text-muted text-center py-10">Signing you in…</p>
        )}

        {state.branchReady &&
          state.authToken &&
          state.dateIsValid &&
          isLoading && (
            <p className="text-muted text-center py-10">Loading visits…</p>
          )}

        {state.branchReady &&
          state.authToken &&
          !isLoading &&
          !errorMessage &&
          hasLoaded &&
          ordered.length === 0 && (
            <p className="text-muted text-center py-10">
              Nobody checked in for a competition at this branch on {date}.
            </p>
          )}

        {!isLoading && !errorMessage && ordered.length > 0 && (
          <>
            {/*
              A filter bar, in the Classes list's idiom: caption under the
              control with the bold verb in front, narrow columns, applied on
              change. It narrows the table below it, so it does not take Edit
              Batch's label-above treatment. `col-lg-3` rather than the bar's
              usual `col-lg-2` because every option here carries a count in
              brackets.

              It is the *filter-bar idiom* rather than the form idiom, but the
              element is still a real `<form>` — which is what `BatchFilter`,
              `BadgeFilters` and `KioskDayPicker` all are, and what the bare
              `<div className="form …">` here was pretending to be. The
              `preventDefault` is the same guard the two CheckIn siblings use:
              there is nothing to submit, so Enter in the bar must not reload
              the page.
            */}
            <form
              className="form form-label-right"
              onSubmit={(event) => event.preventDefault()}
            >
              <div className="form-group row">
                <div className="col-lg-3">
                  <select
                    className="form-control"
                    name="checkoutFilter"
                    aria-label="Filter by Checkout"
                    value={filter}
                    onChange={(event) => setFilter(event.target.value)}
                  >
                    <option value={FILTERS.ALL}>
                      All visits ({ordered.length})
                    </option>
                    <option value={FILTERS.MISSING}>
                      Missing checkout ({missingCount})
                    </option>
                    <option value={FILTERS.COMPLETE}>
                      Checked out ({ordered.length - missingCount})
                    </option>
                  </select>
                  <small className="form-text text-muted">
                    <b>Filter</b> by Checkout
                  </small>
                </div>
                <div className="col-lg-3">
                  <CSVLink
                    className="btn btn-light-primary font-weight-bolder font-size-sm"
                    filename={`Competition_Visits_${branchName.replace(
                      /\s+/g,
                      "_"
                    )}_${date.replace(/-/g, "_")}.csv`}
                    headers={CSV_HEADERS}
                    data={csvData}
                  >
                    Export CSV
                  </CSVLink>
                  <small className="form-text text-muted">
                    Exports the rows shown. A missing checkout exports as an
                    empty cell, never a zero.
                  </small>
                </div>
              </div>
            </form>

            {/*
              The operational signal. One student with no checkout walked out
              without scanning; thirty in one afternoon is a broken scanner or a
              propped-open door, and nobody could see that before this line
              existed. It is stated whether or not the filter is on it.
            */}
            {missingCount > 0 && (
              <div
                className="mb-5 alert alert-custom alert-light-warning"
                role="alert"
              >
                <div className="alert-text">
                  <b>
                    {missingCount} of {ordered.length}
                  </b>{" "}
                  visit{missingCount === 1 ? "" : "s"} on {date} ha
                  {missingCount === 1 ? "s" : "ve"} no checkout. A few means
                  students left without scanning out; a lot on one day usually
                  means the scanner or the door needs looking at.{" "}
                  {filter !== FILTERS.MISSING && (
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline font-weight-bold"
                      onClick={() => setFilter(FILTERS.MISSING)}
                    >
                      Show only these
                    </button>
                  )}
                </div>
              </div>
            )}

            <TotalsLine totals={totals} shownCount={shown.length} />

            {/*
              The head stays put when the filter matches nothing.

              Swapping the whole table out for a sentence left the screen as
              one line of grey text in an empty card, with no sign of what the
              columns had been or that a filter was the reason.

              The portal splits these two cases by *why* the rows are missing,
              not by which table markup is in use:
                - nothing to show, and no control to correct it — the sentence
                  replaces the table (`AttendanceList`'s "No students are
                  enrolled in this class", `BatchCoachAndStudentTables`);
                - a filter or search the reader just set matched nothing — the
                  head stays and the sentence goes inside the body, in a cell
                  spanning the columns. `FeaturedCourses`'
                  `AvailableCoursesTable` does exactly this for "No streams
                  match this search.", and `ClassTable` keeps its `TableHead`
                  up the same way while the body holds a `colSpan` cell.

              This is the second case, written in Bootstrap rather than MUI —
              which is the family both CheckIn tabs already use. The `date`
              and branch empty states above stay sentences, because those are
              the first case.
            */}
            <div className="table-responsive">
              <table className="table table-head-custom table-vertical-center">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th style={{ width: "12%" }}>Batch</th>
                    <th style={{ width: "18%" }}>Checked in</th>
                    <th style={{ width: "18%" }}>Checked out</th>
                    <th style={{ width: "15%" }}>Duration</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.length === 0 ? (
                    <tr>
                      <td className="text-muted text-center py-10" colSpan={5}>
                        No visits match that filter.
                      </td>
                    </tr>
                  ) : (
                    shown.map((visit, index) => (
                      <VisitRow
                        key={`${visit.student_user_id}-${visit.checked_in_at ||
                          index}`}
                        visit={visit}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function VisitRow({ visit }) {
  const minutes = visitDurationMinutes(visit);
  const missing = !visit.checked_out_at;

  return (
    <tr>
      <td className="font-weight-bolder text-dark">
        {studentName(visit) || `Student #${visit.student_user_id}`}
      </td>
      <td className="text-muted font-weight-bold">
        {visit.batch_id === null || visit.batch_id === undefined
          ? "—"
          : visit.batch_id}
      </td>
      <td className="font-weight-bold text-dark">
        {/* The contract always sends a check-in — a visit row exists because
            someone scanned in. "—" is the honest rendering if one ever is
            missing; it is emphatically not the "No Checkout" case. */}
        {visit.checked_in_at ? (
          formatScanTime(visit.checked_in_at)
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td>
        {missing ? (
          <span className="label label-lg label-light-warning label-inline">
            {NO_CHECKOUT}
          </span>
        ) : (
          <span className="font-weight-bold text-dark">
            {formatScanTime(visit.checked_out_at)}
          </span>
        )}
      </td>
      <td>
        {minutes === null ? (
          // No checkout means no duration exists. Showing anything numeric
          // here — a zero, a running clock to now, a guess at closing time —
          // would be a fabricated measurement.
          <span className="text-muted">Not measurable</span>
        ) : (
          <span className="font-weight-bold text-dark">
            {formatDuration(minutes)}
          </span>
        )}
      </td>
    </tr>
  );
}

/**
 * A total that silently drops NULLs is a lie told in a confident font. This one
 * always states what it covers and what it does not, and when everything is
 * excluded it declines to show a number at all rather than print "0h 0m" over
 * a day nobody measured.
 */
function TotalsLine({ totals, shownCount }) {
  if (shownCount === 0) return null;

  if (totals.counted === 0) {
    return (
      <p className="text-muted font-weight-bold font-size-sm mb-5">
        No total: none of these {shownCount} visit
        {shownCount === 1 ? " has" : "s have"} a checkout, so there is no
        duration to add up.
      </p>
    );
  }

  return (
    <p className="text-muted font-weight-bold font-size-sm mb-5">
      Total time in the building:{" "}
      <span className="text-dark">{formatDuration(totals.totalMinutes)}</span>{" "}
      across {totals.counted} visit{totals.counted === 1 ? "" : "s"}
      {totals.excluded > 0 ? (
        <>
          {" "}
          — <b>{totals.excluded}</b> visit
          {totals.excluded === 1 ? "" : "s"} with no checkout{" "}
          {totals.excluded === 1 ? "is" : "are"} excluded from this total.
        </>
      ) : (
        " — none excluded."
      )}
    </p>
  );
}
